import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { NotificationsOutlined } from '@mui/icons-material';
import { Button, Card, IconButton, Typography, Box, Popover } from '@mui/material';
import ArgonBox from "components/ArgonBox";
import HonorariosSkeleton from "components/SkeletonLoader/HonorariosSkeleton";
import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { useUser } from "context/UserContext";

const Honorarios = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, getToken } = useAuth();
  const { honorarios: honorariosContext, loading: userLoading, isDataLoaded, updateLocalData } = useUser();
  const [honorarios, setHonorarios] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [tooltipAnchorEl, setTooltipAnchorEl] = useState(null);

  const profesionalId = localStorage.getItem('id');

  const baseURL = process.env.REACT_APP_BASE_URL;

  const handleNotificationsClick = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleTooltipClick = (event) => {
    setTooltipAnchorEl(event.currentTarget);
  };

  const handleTooltipClose = () => {
    setTooltipAnchorEl(null);
  };




  useEffect(() => {
    // Kickout solo si Firebase confirma que no hay usuario autenticado
    if (!authLoading && !user) {
      navigate("/authentication/sign-in");
      return;
    }
  }, [navigate, user, authLoading]);

  // Cargar honorarios desde el contexto cuando estén disponibles
  useEffect(() => {
    if (isDataLoaded && honorariosContext) {
      setHonorarios(honorariosContext);
    }
  }, [isDataLoaded, honorariosContext]);

  const handleSave = async () => {
    setLoading(true);
    setError("");
    setSuccess(false);
    setValidationError("");
    
    // Validar que los honorarios sean al menos 200 pesos
    if (honorarios < 200) {
      setValidationError("Los honorarios deben ser de al menos $200 pesos por consulta.");
      setLoading(false);
      return;
    }
    
    try {
      const token = await getToken();
      if (!token) {
        setError("No se pudo obtener el token de autenticación. Por favor, inicia sesión nuevamente.");
        setLoading(false);
        navigate("/authentication/sign-in");
        return;
      }

      const payload = {
        honorarios,
      };
      await fetch(`${baseURL}/actualizar-profesional/${profesionalId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      // Actualizar el contexto local
      updateLocalData({ honorarios });
      setSuccess(true);
    } catch (error) {
      setError("Ocurrió un error al guardar los honorarios. Por favor, intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || (userLoading && !isDataLoaded)) {
    return <HonorariosSkeleton />;
  }

  // Si no hay usuario después de cargar, no renderizar nada (el useEffect redirigirá)
  if (!user) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "20px",
        paddingBottom: "100px", // Espacio para el tabbar
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}
    >
      {/* Header con título centrado y notificación */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "400px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "40px",
          marginTop: "20px",
          position: "relative",
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            color: "#000",
            fontSize: { xs: "20px", md: "24px" },
            textAlign: "center",
          }}
        >
          Honorarios
        </Typography>
        <IconButton 
          onClick={handleNotificationsClick}
          sx={{ 
            color: "#666",
            position: "absolute",
            right: 0,
          }}
        >
          <NotificationsOutlined />
        </IconButton>
      </Box>

      {/* Contenedor centrado para la card */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          marginTop: "-95px"
        }}
      >
        {/* Card principal */}
        <Card sx={{ 
          width: '100%', 
          maxWidth: 400, 
          borderRadius: '16px', 
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          backgroundColor: '#ffffff',
          position: 'relative'
        }}>
        <ArgonBox p={4}>
          {/* Header con ícono de información */}
          <ArgonBox display="flex" justifyContent="center" alignItems="center" mb={2} position="relative">
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 'bold',
                color: '#000000',
                fontSize: '18px',
                textAlign: 'center'
              }}
            >
              Modificar honorarios
            </Typography>
            <IconButton 
              onClick={handleTooltipClick}
              sx={{ 
                color: '#4A90E2',
                backgroundColor: 'transparent',
                width: '32px',
                height: '32px',
                position: 'absolute',
                right: 0,
                top: 0,
                '&:hover': {
                  backgroundColor: 'rgba(74, 144, 226, 0.1)'
                }
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </ArgonBox>

          {/* Descripción */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#000000',
              fontSize: '14px',
              lineHeight: '1.4',
              marginBottom: '24px',
              textAlign: 'center'
            }}
          >
            Definí el valor que quieras cobrar por tus consultas.
          </Typography>

          {/* Controles de precio */}
          <ArgonBox 
            display="flex" 
            alignItems="center" 
            justifyContent="center" 
            gap={3} 
            mb={4}
          >
            <IconButton 
              onClick={() => setHonorarios((prev) => Math.max(200, prev - 50))} 
              disabled={loading}
              sx={{
                backgroundColor: 'transparent',
                color: '#000000',
                width: '40px',
                height: '40px',
                border: 'none',
                fontSize: '24px',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)'
                },
                '&:disabled': {
                  backgroundColor: 'transparent',
                  color: '#cccccc'
                }
              }}
            >
              <RemoveIcon sx={{ fontSize: '24px' }} />
            </IconButton>
            
            <ArgonBox display="flex" flexDirection="column" alignItems="center">
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#4CAF50',
                  fontWeight: 'bold',
                  fontSize: '34px',
                  textAlign: 'center',
                  margin: 0
                }}
              >
                ${honorarios}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#000000',
                  fontSize: '14px',
                  textAlign: 'center',
                  margin: 0
                }}
              >
                por consulta
              </Typography>
            </ArgonBox>
            
            <IconButton 
              onClick={() => setHonorarios((prev) => prev + 50)} 
              disabled={loading}
              sx={{
                backgroundColor: 'transparent',
                color: '#000000',
                width: '40px',
                height: '40px',
                border: 'none',
                fontSize: '24px',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)'
                },
                '&:disabled': {
                  backgroundColor: 'transparent',
                  color: '#cccccc'
                }
              }}
            >
              <AddIcon sx={{ fontSize: '24px' }} />
            </IconButton>
          </ArgonBox>

          {/* Botón Guardar */}
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading}
            fullWidth
            sx={{ 
              backgroundColor: '#1F85F6',
              color: '#ffffff',
              fontSize: '16px',
              fontWeight: 'bold',
              padding: '14px',
              borderRadius: '12px',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#1A73D9',
                boxShadow: 'none'
              },
              '&:disabled': {
                backgroundColor: '#cccccc',
                color: '#ffffff'
              }
            }}
          >
            {loading ? "Guardando..." : "Guardar"}
          </Button>

          {/* Mensajes de estado */}
          {success && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#4CAF50',
                textAlign: 'center',
                marginTop: '16px',
                fontSize: '12px'
              }}
            >
              Honorarios actualizados exitosamente.
            </Typography>
          )}

          {error && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#f44336',
                textAlign: 'center',
                marginTop: '16px',
                fontSize: '12px'
              }}
            >
              {error}
            </Typography>
          )}

          {validationError && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#f44336',
                textAlign: 'center',
                marginTop: '16px',
                fontSize: '12px'
              }}
            >
              {validationError}
            </Typography>
          )}
        </ArgonBox>
        </Card>
      </Box>

      {/* Popover de notificaciones */}
      <Popover
        open={Boolean(notificationsAnchorEl)}
        anchorEl={notificationsAnchorEl}
        onClose={handleNotificationsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          zIndex: 9999,
          '& .MuiPopover-paper': {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            minWidth: '200px',
            zIndex: 9999,
          }
        }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
            No hay notificaciones pendientes
          </Typography>
        </Box>
      </Popover>

      {/* Popover del tooltip de información */}
      <Popover
        open={Boolean(tooltipAnchorEl)}
        anchorEl={tooltipAnchorEl}
        onClose={handleTooltipClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        sx={{
          zIndex: 9999,
          '& .MuiPopover-paper': {
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            padding: '12px 16px',
            maxWidth: '280px',
            zIndex: 9999,
          }
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#333', fontSize: '14px', fontWeight: '500' }}>
            Los honorarios se incrementan/disminuyen de a $50
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
};

export default Honorarios;
